/* eslint-disable no-param-reassign */
// vuex.vuejs.org/guide/#the-simplest-store
import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";

import EnsureCloud from "./helpers/ensure-cloud";
import { publishExpertCoupon, clearExpertCoupon } from "./helpers/expert-coupon";
import {
  setGoogleUserData,
  reportPendingPurchase,
} from "./helpers/ads-conversions";

const platform = () => {
  if (window.navigator.userAgent.indexOf("Windows") !== -1) {
    return "Windows";
  }
  if (window.navigator.userAgent.indexOf("Mac") !== -1) {
    return "Mac";
  }
  if (window.navigator.userAgent.indexOf("X11") !== -1) {
    return "Unix";
  }
  if (window.navigator.userAgent.indexOf("Linux") !== -1) {
    return "Linux";
  }
  return "";
};

export default createStore({
  state() {
    return {
      betaCode: null,
      teamInviteCode: null,
      hasBetaCodeError: false,
      user: null,
      token: null,
      // Admin-only "View as team": the team an admin is currently impersonating.
      impersonateTeam: null,
      loadingProjects: false,
      projects: [],
      platform: platform(),
      // Shared filters across analytics, runs, and cases pages
      sharedFilters: {
        search: "",
        suite: "",
        filename: "",
        branch: "",
        repo: "",
        commit: "",
        timePeriod: "7d",
        status: "",
        platform: "",
        duration: "",
      },
    };
  },
  mutations: {
    setGoogleAdsEligibility: (state, eligibility) => {
      if (state.user?.team && typeof state.user.team === "object") {
        state.user.team.googleAdsEligibility = eligibility;
        setGoogleUserData(state.user);
      }
    },
    // /start re-scores the team; keep the path that values Begin checkout and
    // Trial started current without waiting for the next whoami.
    setOnboardingQualification: (state, qualification) => {
      if (state.user?.team && typeof state.user.team === "object" && qualification) {
        state.user.team.onboardingQualification = {
          ...(state.user.team.onboardingQualification || {}),
          recommendedPath: qualification.recommendedPath ?? null,
          signals: qualification.signals || {},
        };
      }
    },
    setUser: (state, user) => {
      state.user = user;
      // Hand the marketing site the discount code; it has no session of its own.
      publishExpertCoupon(user);
      // Enhanced Conversions: later Google Ads conversions carry the hashed email.
      setGoogleUserData(user);
      // Browser pixels for a purchase no browser saw (a trial converting to
      // paid); no-op unless the server has recorded one not yet reported here.
      reportPendingPurchase(user);
    },
    setToken: (state, token) => {
      state.token = token;
    },
    clearUser: (state) => {
      state.user = null;
      setGoogleUserData(null);
      clearExpertCoupon();
    },
    clearToken: (state) => {
      state.token = null;
    },
    // Persist the impersonation target to a dedicated localStorage key so the
    // Cloud SDK can attach the `x-impersonate-team` header on every request.
    setImpersonateTeam: (state, team) => {
      state.impersonateTeam = team;
      try {
        if (team && team.id) {
          window.localStorage.setItem("td_impersonate_team", team.id);
        } else {
          window.localStorage.removeItem("td_impersonate_team");
        }
      } catch (e) {
        /* localStorage unavailable */
      }
    },
    clearImpersonateTeam: (state) => {
      state.impersonateTeam = null;
      try {
        window.localStorage.removeItem("td_impersonate_team");
      } catch (e) {
        /* localStorage unavailable */
      }
    },
    setProjects: (state, projects) => {
      state.projects = projects;
    },
    updateProject: (state, updatedProject) => {
      const projectIdx = _.findIndex(state.projects, {
        id: updatedProject.id,
      });
      if (projectIdx !== -1) {
        state.projects[projectIdx] = updatedProject;
      }
    },
    setLoadingProjects: (state) => {
      state.loadingProjects = true;
    },
    setCompleteLoadingProjects: (state) => {
      state.loadingProjects = false;
    },
    resetApp: (state) => {
      clearExpertCoupon();
      state.user = null;
      state.token = null;
      state.projects = [];
      state.loadingProjects = false;
      state.hasBetaCodeError = false;
      state.impersonateTeam = null;
      try {
        window.localStorage.removeItem("td_impersonate_team");
      } catch (e) {
        /* localStorage unavailable */
      }
    },
    setBetaCode: (state, betaCode) => {
      state.betaCode = betaCode;
    },
    clearBetaCode: (state) => {
      state.betaCode = "";
    },
    setTeamInviteCode: (state, teamInviteCode) => {
      state.teamInviteCode = teamInviteCode;
    },
    clearTeamInviteCode: (state) => {
      state.teamInviteCode = "";
    },
    setBetaCodeError: (state) => {
      state.hasBetaCodeError = true;
    },
    clearBetaCodeError: (state) => {
      state.hasBetaCodeError = false;
    },
    setSharedFilters: (state, filters) => {
      state.sharedFilters = { ...state.sharedFilters, ...filters };
    },
    clearSharedFilters: (state) => {
      state.sharedFilters = {
        search: "",
        suite: "",
        filename: "",
        branch: "",
        repo: "",
        commit: "",
        timePeriod: "7d",
        status: "",
        platform: "",
        duration: "",
      };
    },
  },
  getters: {
    getProjectById: (state) => (projectId) =>
      state.projects.find((project) => project.id === projectId),
  },
  actions: {
    async fetchProjects({ commit, state }) {
      await EnsureCloud();

      commit("setLoadingProjects");

      const projects = await Cloud.projectsGet({
        team: state.user.team.id,
      })
        .headers({
          Authorization: `Bearer ${state.token}`,
        })
        .tolerate((err) => {
          console.error(err);
        });

      if (Array.isArray(projects)) {
        commit("setProjects", projects);
        commit("setCompleteLoadingProjects");
      }
    },
    async checkInvite({ commit, state }, inviteCode) {
      await EnsureCloud();

      try {
        const updatedUser = await Cloud.validateInvite({
          code: inviteCode,
        })
          .headers({
            Authorization: `Bearer ${state.token}`,
          })
          .tolerate((err) => {
            console.error(err);
          });

        if (updatedUser) {
          commit("setUser", updatedUser);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async checkBeta({ commit, state }, betaCode) {
      await EnsureCloud();

      try {
        const updatedUser = await Cloud.validateBeta({
          code: betaCode,
        })
          .headers({
            Authorization: `Bearer ${state.token}`,
          })
          .tolerate((err) => {
            console.error(err);
            commit("setBetaCodeError");
          });

        if (updatedUser) {
          commit("setUser", updatedUser);
        }

        return updatedUser;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    async editTeamName({ commit, state }, teamName) {
      await EnsureCloud();

      const updatedUser = await Cloud.teamEditName({
        name: teamName,
      })
        .headers({
          Authorization: `Bearer ${state.token}`,
        })
        .tolerate((err) => {
          console.error(err);
          commit("setBetaCodeError");
        });

      if (updatedUser) {
        commit("setUser", updatedUser);
      }
    },
    async setHasOnboarded({ commit, state }) {
      await EnsureCloud();

      const updatedUser = await Cloud.userSetOnboard()
        .headers({
          Authorization: `Bearer ${state.token}`,
        })
        .tolerate((err) => {
          console.error(err);
        });

      if (updatedUser) {
        commit("setUser", updatedUser);
      }
    },
  },
  plugins: !window.location.href.endsWith("embed=true")
    ? [
        createPersistedState({
          key: "replayable",
        }),
      ]
    : [],
});
